/**
 * Single choke-point for reporting a status/phase to the host. Every other file in
 * `signaling/` that needs to report something calls `emitStatus` — nothing emits
 * directly through the host's `opts` callbacks on its own, so there is exactly one
 * place that knows how a status code and a lifecycle phase turn into host callbacks.
 * Kept as its own file (rather than living in `operator-session.ts`) specifically so
 * every phase file can import it without creating a circular import back into the
 * controller.
 *
 * Takes `opts` directly (not a wrapping session/handle object) — that's genuinely all
 * this function ever needed.
 */

import type { SessionPhaseValue, StatusCodeValue } from "../status-codes";

export interface EmitStatusOpts {
  setStatus: (code: StatusCodeValue) => void;
  onState?: (phase: SessionPhaseValue) => void;
}

/** Emit a fine-grained status code and, optionally, a lifecycle phase. The package emits
 * only string codes — the host maps them to localized/human-readable text. */
export function emitStatus(opts: EmitStatusOpts, code: StatusCodeValue, phase?: SessionPhaseValue) {
  opts.setStatus(code);
  if (phase) opts.onState?.(phase);
}
